import React, { useState } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { 
  FiFilter, FiSearch, FiUser, FiClock, 
  FiUserCheck, FiUserX, FiChevronDown, FiMessageSquare,
  FiAlertCircle, FiCheck, FiEye
} from 'react-icons/fi';

interface Expert {
  id: string;
  name: string;
  specialty: string[];
  status: 'available' | 'busy' | 'offline';
  activeChatCount: number;
}

interface ExpertRequest {
  id: string;
  user: {
    id: string;
    name: string;
    accountType: 'standard' | 'premium' | 'business';
  };
  topic: string;
  requestTime: Date;
  status: 'pending' | 'assigned' | 'completed' | 'cancelled';
  priority: 'high' | 'medium' | 'low';
  assignedTo?: string;
  description: string;
  chatId: string;
}

const AdminExpertRequests: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<string | null>('req1');
  const [showAssignModal, setShowAssignModal] = useState(false);
  
  // Mock experts data
  const experts: Expert[] = [
    { 
      id: 'exp1', 
      name: 'Aadi Srivastava', 
      specialty: ['Investments', 'Retirement Planning'], 
      status: 'available',
      activeChatCount: 1
    },
    { 
      id: 'exp2', 
      name: 'Priya Mehta', 
      specialty: ['Debt Management', 'Budgeting'], 
      status: 'available',
      activeChatCount: 0
    },
    { 
      id: 'exp3', 
      name: 'Michael Johnson', 
      specialty: ['Tax Planning', 'Estate Planning'], 
      status: 'busy',
      activeChatCount: 3
    },
    { 
      id: 'exp4', 
      name: 'Sophie Yang', 
      specialty: ['Investments', 'International Finance'], 
      status: 'offline',
      activeChatCount: 0
    },
  ];
  
  // Mock expert requests
  const expertRequests: ExpertRequest[] = [
    {
      id: 'req1',
      user: {
        id: 'user1',
        name: 'Sarah Johnson',
        accountType: 'premium'
      },
      topic: 'Investment Portfolio Review',
      requestTime: new Date('2023-07-10T14:40:00'),
      status: 'pending',
      priority: 'high',
      description: 'User is 35 years old, looking for advice on portfolio allocation. Currently has stocks and bonds but concerned about market volatility.',
      chatId: 'chat1'
    },
    {
      id: 'req2',
      user: {
        id: 'user3',
        name: 'Emily Rodriguez',
        accountType: 'standard'
      },
      topic: 'Transaction Issues',
      requestTime: new Date('2023-07-10T12:23:00'),
      status: 'assigned',
      priority: 'high',
      assignedTo: 'Aadi Srivastava',
      description: 'User reports missing transactions. Internal transfer from checking to savings account is not showing up after 24 hours.',
      chatId: 'chat3'
    },
    {
      id: 'req3',
      user: {
        id: 'user6',
        name: 'John Williams',
        accountType: 'business'
      },
      topic: 'Business Loan Application',
      requestTime: new Date('2023-07-10T10:15:00'),
      status: 'pending',
      priority: 'medium',
      description: 'Small business owner seeking information about loan options for expansion. Currently employs 10 people and has been in business for 5 years.',
      chatId: 'chat6'
    },
    {
      id: 'req4',
      user: {
        id: 'user8',
        name: 'Amanda Peters',
        accountType: 'premium'
      },
      topic: 'Retirement Planning',
      requestTime: new Date('2023-07-09T15:30:00'),
      status: 'completed',
      priority: 'medium',
      assignedTo: 'Michael Johnson',
      description: 'User is 55 and wanting to ensure retirement savings are on track. Concerned about market downturns affecting retirement timeline.',
      chatId: 'chat8'
    },
    {
      id: 'req5',
      user: {
        id: 'user10',
        name: 'Carlos Martinez',
        accountType: 'standard'
      },
      topic: 'Credit Score Improvement',
      requestTime: new Date('2023-07-09T11:45:00'),
      status: 'cancelled',
      priority: 'low',
      description: 'User wants advice on improving credit score. Currently at 620 and wants to qualify for better rates on home loan.',
      chatId: 'chat10'
    }
  ];
  
  const filteredRequests = expertRequests.filter(request => {
    // Apply search filter
    if (searchQuery && !request.user.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !request.topic.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Apply status filter
    if (filter !== 'all' && request.status !== filter) {
      return false;
    }
    
    return true;
  });
  
  const currentRequest = selectedRequest 
    ? expertRequests.find(req => req.id === selectedRequest) 
    : null;
  
  const availableExperts = experts.filter(expert => 
    expert.status !== 'offline' && expert.activeChatCount < 3
  );
  
  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Today, ${formatTime(date)}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${formatTime(date)}`;
    } else {
      return `${date.toLocaleDateString()} ${formatTime(date)}`;
    }
  };
  
  return (
    <AdminLayout>
      <div className="min-h-screen bg-background overflow-hidden">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <h1 className="text-2xl font-bold">Expert Requests</h1>
            
            <div className="flex items-center space-x-3 mt-4 md:mt-0">
              <div className="relative">
                <input
                  type="text"
                  className="bg-card border border-border rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-primary w-64"
                  placeholder="Search requests..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <FiSearch className="absolute left-3 top-2.5 text-text/50" size={18} />
              </div>
              
              <div className="relative">
                <select
                  className="bg-card border border-border rounded-lg px-4 py-2 pr-10 appearance-none focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Requests</option>
                  <option value="pending">Pending</option>
                  <option value="assigned">Assigned</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <FiChevronDown className="absolute right-3 top-3 text-text/50" size={16} />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Requests list */}
            <div className="lg:col-span-1">
              <div className="bg-foreground border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="px-6 py-4 border-b border-border">
                  <h2 className="text-lg font-semibold">Request Queue</h2>
                </div>
                
                <div className="overflow-y-auto max-h-[calc(100vh-220px)]">
                  {filteredRequests.length === 0 ? (
                    <div className="p-6 text-center text-text/50">
                      <FiAlertCircle size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No requests match your filters</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredRequests.map((request) => (
                        <div 
                          key={request.id}
                          className={`p-4 hover:bg-card/50 cursor-pointer transition-colors ${
                            selectedRequest === request.id ? 'bg-card/70' : ''
                          }`}
                          onClick={() => setSelectedRequest(request.id)}
                        >
                          <div className="flex items-start">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium mr-3">
                              {request.user.name.charAt(0)}
                            </div>
                            
                            <div className="flex-grow min-w-0">
                              <div className="flex items-center justify-between">
                                <h3 className="font-medium truncate">{request.user.name}</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  request.user.accountType === 'premium' 
                                    ? 'bg-purple-100 text-purple-800' 
                                    : request.user.accountType === 'business'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {request.user.accountType}
                                </span>
                              </div>
                              
                              <p className="text-sm font-medium mt-1 truncate">{request.topic}</p>
                              
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-xs text-text/50 flex items-center">
                                  <FiClock size={12} className="mr-1" />
                                  {formatDate(new Date(request.requestTime))}
                                </span>
                                
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  request.status === 'pending' 
                                    ? 'bg-yellow-100 text-yellow-800' 
                                    : request.status === 'assigned'
                                    ? 'bg-blue-100 text-blue-800'
                                    : request.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {request.status}
                                </span>
                              </div>
                              
                              {request.assignedTo && (
                                <div className="flex items-center mt-1.5">
                                  <FiUser size={12} className="text-text/50 mr-1" />
                                  <span className="text-xs text-text/70">{request.assignedTo}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Request details */}
            <div className="lg:col-span-2">
              {currentRequest ? (
                <div className="bg-foreground border border-border rounded-xl shadow-sm h-full">
                  <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Request Details</h2>
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-2 ${
                        currentRequest.priority === 'high' 
                          ? 'bg-red-100 text-red-800' 
                          : currentRequest.priority === 'medium'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {currentRequest.priority} priority
                      </span>
                      
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        currentRequest.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : currentRequest.status === 'assigned'
                          ? 'bg-blue-100 text-blue-800'
                          : currentRequest.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {currentRequest.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    {/* User info */}
                    <div className="flex items-start mb-6">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-medium mr-4">
                        {currentRequest.user.name.charAt(0)}
                      </div>
                      
                      <div>
                        <div className="flex items-center">
                          <h3 className="text-lg font-medium">{currentRequest.user.name}</h3>
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                            currentRequest.user.accountType === 'premium' 
                              ? 'bg-purple-100 text-purple-800' 
                              : currentRequest.user.accountType === 'business'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {currentRequest.user.accountType}
                          </span>
                        </div>
                        <p className="text-text/70">{currentRequest.topic}</p>
                        <p className="text-sm text-text/50 mt-1">
                          Requested {formatDate(new Date(currentRequest.requestTime))}
                        </p>
                      </div>
                    </div>
                    
                    {/* Request information */}
                    <div className="bg-card/30 rounded-lg p-4 mb-6">
                      <h4 className="font-medium mb-2">Request Description</h4>
                      <p className="text-text/80 mb-4">{currentRequest.description}</p>
                      
                      <div className="flex items-center text-sm text-text/70">
                        <FiMessageSquare size={16} className="mr-1.5" />
                        <span>From chat conversation #{currentRequest.chatId}</span>
                      </div>
                    </div>
                    
                    {/* Assignment section */}
                    {currentRequest.status === 'pending' ? (
                      <div>
                        <h4 className="font-medium mb-3">Assign to Expert</h4>
                        
                        <div className="space-y-3 mb-4">
                          {availableExperts.length > 0 ? (
                            availableExperts.map(expert => (
                              <div 
                                key={expert.id} 
                                className="flex items-center justify-between p-3 bg-card/40 rounded-lg border border-border hover:border-primary/30 cursor-pointer"
                              >
                                <div className="flex items-center">
                                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium mr-3">
                                    {expert.name.charAt(0)}
                                  </div>
                                  
                                  <div>
                                    <div className="font-medium">{expert.name}</div>
                                    <div className="text-xs text-text/70">
                                      {expert.specialty.join(', ')} • {expert.activeChatCount} active chats
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center">
                                  <div className={`w-2 h-2 rounded-full ${
                                    expert.status === 'available' ? 'bg-green-500' : 'bg-yellow-500'
                                  } mr-1.5`}></div>
                                  <span className="text-sm text-text/70 mr-3">{expert.status}</span>
                                  
                                  <button className="p-1.5 bg-primary text-white rounded-lg hover:bg-primary/90">
                                    <FiUserCheck size={16} />
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center p-4 bg-card/30 rounded-lg">
                              <p className="text-text/70">No experts are currently available</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : currentRequest.status === 'assigned' ? (
                      <div>
                        <h4 className="font-medium mb-3">Assignment Information</h4>
                        
                        <div className="p-4 bg-card/40 rounded-lg border border-border mb-4">
                          <div className="flex items-center mb-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium mr-3">
                              {currentRequest.assignedTo?.charAt(0)}
                            </div>
                            
                            <div>
                              <div className="font-medium">{currentRequest.assignedTo}</div>
                              <div className="text-xs text-text/70">
                                Assigned {formatDate(new Date(currentRequest.requestTime))}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex justify-between mt-2">
                            <button className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg flex items-center">
                              <FiEye size={16} className="mr-1.5" />
                              View Chat
                            </button>
                            
                            <button className="px-3 py-1.5 text-sm bg-red-100 text-red-800 rounded-lg flex items-center">
                              <FiUserX size={16} className="mr-1.5" />
                              Reassign
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : currentRequest.status === 'completed' ? (
                      <div>
                        <h4 className="font-medium mb-3">Resolution Information</h4>
                        
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-800 mr-3">
                              <FiCheck size={16} />
                            </div>
                            
                            <div>
                              <div className="font-medium text-green-800">Request Completed</div>
                              <div className="text-sm text-green-700">
                                This request was successfully handled by {currentRequest.assignedTo}
                              </div>
                            </div>
                          </div>
                          
                          <button className="mt-3 px-3 py-1.5 text-sm bg-white border border-border rounded-lg flex items-center">
                            <FiEye size={16} className="mr-1.5" />
                            View Resolution Details
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-medium mb-3">Cancellation Information</h4>
                        
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-800 mr-3">
                              <FiUserX size={16} />
                            </div>
                            
                            <div>
                              <div className="font-medium text-gray-800">Request Cancelled</div>
                              <div className="text-sm text-gray-700">
                                This request was cancelled or timed out
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Action buttons */}
                    <div className="flex justify-end mt-6">
                      <button className="px-4 py-2 bg-card border border-border rounded-lg mr-3">
                        View User Profile
                      </button>
                      
                      <button className="px-4 py-2 bg-primary text-white rounded-lg">
                        Go to Chat
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-foreground border border-border rounded-xl shadow-sm h-full flex items-center justify-center p-8">
                  <div className="text-center max-w-md">
                    <FiAlertCircle size={48} className="mx-auto mb-4 text-text/30" />
                    <h2 className="text-xl font-semibold mb-2">No request selected</h2>
                    <p className="text-text/70">
                      Select a request from the list to view details and take action.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminExpertRequests; 